import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  ScrollView,
  Input,
  Center,
  Image,
  StatusBar,
  Button,
  useToast,
  Stack,
  Pressable as NBPressable,
  Icon,
} from "native-base";
import { LinearGradient } from "expo-linear-gradient";
import { Pressable, Keyboard, Platform } from "react-native";
import { MaterialIcons } from "@expo/vector-icons";
import { Ionicons } from "@expo/vector-icons";
import { useNavigation } from "@react-navigation/native";
import { useSelector } from "react-redux";
import axios from "axios";
import { GlobalConstants } from "../../constants/constants";
import { GlobalStyles } from "../../constants/styles";
import ToastMessage from "../../components/Toast";

const BASE_URL = `${GlobalConstants.SurveyBaseUrl}`.replace(/\/$/, "");

const EDIT_BOX_SIZE = 40;
const EDIT_GAP = 12;
const RIGHT_SPACE = EDIT_BOX_SIZE + EDIT_GAP;
const STAR_COL_WIDTH = 170;
const BUTTON_WIDTH = 140;
const ROW_HEIGHT = 40;
const STAT_BOX_SIZE = 40;
const STAT_BOX_MARGIN_VERTICAL = Math.round((ROW_HEIGHT - STAT_BOX_SIZE) / 2);

const topStatImg = require("../../../assets/statSurvey.png");
const bottomStatImg = require("../../../assets/surveyAnalytics.png");

const HomeButton = ({ onPress }) => {
  return (
    <Pressable onPress={onPress}>
      <HStack
        alignItems="center"
        space={2}
        bg="rgba(3, 0, 87, 0.1)"
        px={4}
        py={2}
        borderRadius={8}
      >
        <Icon
          as={Ionicons}
          name="home"
          size="md"
          color={GlobalStyles.colors.blue}
        />
        <Text
          fontSize="14px"
          fontWeight="500"
          color={GlobalStyles.colors.blue}
          fontFamily={GlobalStyles.text.fontFamily}
        >
          Home
        </Text>
      </HStack>
    </Pressable>
  );
};

const GradientButton = ({ children, onPress }) => {
  return (
    <Box
      borderRadius={10}
      overflow="hidden"
      borderWidth={1}
      borderColor={GlobalStyles.colors.black}
      bg={GlobalStyles.colors.white}
      w={["100%", "100%", `${BUTTON_WIDTH}px`]}
    >
      <LinearGradient
        colors={[
          GlobalStyles.colors.gradientBlueStart ?? "#000561",
          GlobalStyles.colors.gradientBlueEnd ?? "#000AC7",
        ]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 0 }}
        style={{
          width: "100%",
          height: 44,
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 10,
        }}
      >
        <Pressable
          onPress={onPress}
          style={{
            width: "100%",
            height: "100%",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Text
            color={GlobalStyles.colors.mustard}
            fontWeight="500"
            fontSize="14px"
            letterSpacing="0.1px"
            lineHeight="20px"
            fontFamily={GlobalStyles.text.fontFamily}
            textAlign="center"
          >
            {children}
          </Text>
        </Pressable>
      </LinearGradient>
    </Box>
  );
};

const EditIcon = ({ onPress }) => (
  <Pressable
    onPress={onPress}
    style={{
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: GlobalStyles.colors.white,
      borderWidth: 1,
      width: EDIT_BOX_SIZE,
      height: ROW_HEIGHT,
      borderColor: GlobalStyles.colors.black,
      borderRadius: 10,
    }}
    hitSlop={6}
  >
    <Image
      source={require("../../../assets/carbon_edit.png")}
      alt="edit"
      size="20px"
      resizeMode="contain"
    />
  </Pressable>
);

const DeleteIcon = ({ onPress }) => (
  <Pressable
    onPress={onPress}
    style={{
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: GlobalStyles.colors.white,
      borderWidth: 1,
      width: EDIT_BOX_SIZE,
      height: ROW_HEIGHT,
      borderColor: GlobalStyles.colors.red || "#FF0000",
      borderRadius: 10,
    }}
    hitSlop={6}
  >
    <MaterialIcons name="delete" size={20} color={GlobalStyles.colors.red || "#FF0000"} />
  </Pressable>
);

const SurveyRow = ({
  value,
  onChange,
  placeholder,
  isTitle,
  isSubtitle,
  isEditing,
  onEditPress,
  onSubmitEditing,
  onDeletePress,
  showDelete = false,
}) => {
  const rowHeight = ROW_HEIGHT;
  const fontSize = isTitle ? 24 : isSubtitle ? 18 : 14;
  const fontWeight = isTitle || isSubtitle ? "500" : "400";

  return (
    <HStack w="100%" alignItems="center" space={3} mb={2}>
      <Box flex={1}>
        <Input
          value={value}
          onChangeText={onChange}
          placeholder={placeholder}
          isReadOnly={!isEditing}
          borderRadius="10px"
          borderColor={
            isEditing ? GlobalStyles.colors.mustard : GlobalStyles.colors.black
          }
          borderWidth={1}
          bg={GlobalStyles.colors.white}
          px={4}
          color={GlobalStyles.colors.black}
          placeholderTextColor={GlobalStyles.colors.black}
          fontSize={`${fontSize}px`}
          fontWeight={fontWeight}
          letterSpacing="0.1px"
          lineHeight="20px"
          paddingTop={0}
          paddingBottom={0}
          scrollEnabled={false}
          _input={{
            fontFamily: GlobalStyles.text.fontFamily,
            multiline: false,
            numberOfLines: 1,
            scrollEnabled: false,
            spellCheck: false,
            includeFontPadding: false,
            autoCorrect: false,
            autoComplete: "off",
            importantForAutofill: "no",
            returnKeyType: "done",
            onSubmitEditing: () => {
              if (onSubmitEditing) onSubmitEditing();
              Keyboard.dismiss();
            },
            style: {
              height: rowHeight,
              lineHeight: rowHeight,
              paddingTop: 0,
              paddingBottom: 0,
              textAlignVertical: "center",
            },
          }}
          style={{
            height: rowHeight,
            lineHeight: rowHeight,
            paddingTop: 0,
            paddingBottom: 0,
            textAlignVertical: "center",
            overflow: "hidden",
          }}
        />
      </Box>

      <EditIcon onPress={onEditPress} />
      {showDelete && <DeleteIcon onPress={onDeletePress} />}
    </HStack>
  );
};

const StarRow = ({ value = 0 }) => {
  const stars = [1, 2, 3, 4, 5];
  return (
    <HStack space={2} alignItems="center" justifyContent="flex-end">
      {stars.map((s) => (
        <MaterialIcons
          key={s}
          name={s <= value ? "star" : "star-border"}
          size={28}
          color={
            s <= value ? GlobalStyles.colors.mustard : GlobalStyles.colors.black
          }
        />
      ))}
    </HStack>
  );
};

const PreviewRow = ({ text, withStars, stars, isTitle }) => {
  const rowHeight = ROW_HEIGHT;
  return (
    <HStack
      w="100%"
      alignItems="center"
      justifyContent="space-between"
      mb={2}
      style={{ minHeight: rowHeight }}
    >
      <Box
        flex={1}
        borderWidth={1}
        borderColor={GlobalStyles.colors.black}
        borderRadius={8}
        bg={GlobalStyles.colors.white}
        px={4}
        h={`${rowHeight}px`}
        justifyContent="center"
      >
        <Text
          color={GlobalStyles.colors.black}
          fontSize="16px"
          fontWeight={isTitle ? "500" : "400"}
          fontFamily={GlobalStyles.text.fontFamily}
        >
          {text}
        </Text>
      </Box>

      <Box
        w={[0, `${STAR_COL_WIDTH}px`, `${STAR_COL_WIDTH}px`]}
        display={["none", "flex", "flex"]}
        ml={4}
        justifyContent="center"
        alignItems="flex-end"
        h={`${ROW_HEIGHT}px`}
      >
        {withStars ? <StarRow value={stars} /> : null}
      </Box>
    </HStack>
  );
};

const AnalyticsStatBox = ({ topImage, bottomImage, onPress }) => {
  const SIZE = STAT_BOX_SIZE;
  const verticalOffset = STAT_BOX_MARGIN_VERTICAL;

  return (
    <Pressable onPress={onPress}>
      <LinearGradient
        colors={[
          GlobalStyles.colors.statGradientTop ?? "rgba(0,33,105,0.85)",
          GlobalStyles.colors.statGradientBottom ?? "#00A4DF",
        ]}
        start={{ x: 0.5, y: 0 }}
        end={{ x: 0.5, y: 1 }}
        style={{
          width: SIZE,
          height: SIZE,
          borderRadius: 6,
          alignItems: "center",
          justifyContent: "center",
          marginTop: verticalOffset,
          marginBottom: verticalOffset,
        }}
      >
        <VStack
          alignItems="center"
          justifyContent="center"
          style={{ width: "100%", height: "100%" }}
        >
          <Image
            source={topImage}
            alt="stat-top"
            style={{
              width: SIZE - 8,
              height: Math.floor(SIZE * 0.5) - 2,
              marginTop: 0,
            }}
            resizeMode="contain"
          />
          <Image
            source={bottomImage}
            alt="stat-bottom"
            style={{
              width: SIZE - 10,
              height: Math.floor(SIZE * 0.5) - 2,
              marginBottom: 0,
            }}
            resizeMode="contain"
          />
        </VStack>
      </LinearGradient>
    </Pressable>
  );
};

const CalendarInline = ({ initialStart, initialEnd, onApply, onClose }) => {
  const today = useMemo(() => new Date(), []);
  const initialBase = initialStart ? new Date(initialStart) : today;

  const [viewYear, setViewYear] = useState(initialBase.getFullYear());
  const [viewMonth, setViewMonth] = useState(initialBase.getMonth());
  const [start, setStart] = useState(
    initialStart ? new Date(initialStart) : null,
  );
  const [end, setEnd] = useState(initialEnd ? new Date(initialEnd) : null);

  useEffect(() => {
    const base = initialStart ? new Date(initialStart) : new Date();
    setViewYear(base.getFullYear());
    setViewMonth(base.getMonth());
    setStart(initialStart ? new Date(initialStart) : null);
    setEnd(initialEnd ? new Date(initialEnd) : null);
  }, [initialStart, initialEnd]);

  const addMonths = useCallback((y, m, delta) => {
    const d = new Date(y, m + delta, 1);
    return [d.getFullYear(), d.getMonth()];
  }, []);

  const formatMonthTitle = useCallback((year, month) => {
    const dt = new Date(year, month, 1);
    return dt.toLocaleString(undefined, { month: "long" });
  }, []);

  const monthGrid = useCallback((year, month) => {
    const firstDay = new Date(year, month, 1);
    const firstWeekday = firstDay.getDay();
    const offset = (firstWeekday + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const cells = [];
    for (let i = 0; i < offset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);

    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
    return weeks;
  }, []);

  const isSameDay = (a, b) => {
    if (!a || !b) return false;
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  };

  const inRange = (d) => {
    if (!start || !end || !d) return false;
    const dd = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const s = new Date(
      start.getFullYear(),
      start.getMonth(),
      start.getDate(),
    ).getTime();
    const e = new Date(
      end.getFullYear(),
      end.getMonth(),
      end.getDate(),
    ).getTime();
    return dd > s && dd < e;
  };

  const handleSelect = (d) => {
    if (!d) return;
    if (!start || (start && end)) {
      setStart(d);
      setEnd(null);
      return;
    }
    if (d.getTime() < start.getTime()) {
      setStart(d);
      setEnd(null);
      return;
    }
    if (isSameDay(d, start)) {
      setEnd(d);
      return;
    }
    setEnd(d);
  };

  const renderMonth = (year, month) => {
    const weeks = monthGrid(year, month);
    const weekdayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    const darkBlue = GlobalStyles.colors.gradientBlueStart ?? "#000561";
    const lightRange =
      GlobalStyles.colors.calendarRange ?? "rgba(0,150,255,0.18)";

    return (
      <Box
        key={`${year}-${month}`}
        w={["100%", "100%", "50%"]}
        px={2}
        mb={[4, 4, 0]}
      >
        <HStack justifyContent="center" alignItems="center" mb={2}>
          <Text
            fontSize={["13px", "14px", "14px"]}
            fontWeight="600"
            color={GlobalStyles.colors.black}
            fontFamily={GlobalStyles.text.fontFamily}
          >
            {formatMonthTitle(year, month)} {year}
          </Text>
        </HStack>

        <HStack justifyContent="space-between" pb={1}>
          {weekdayLabels.map((w) => (
            <Text
              key={w}
              fontSize={["11px", "12px", "12px"]}
              w="14%"
              textAlign="center"
              color={GlobalStyles.colors.black}
              opacity={0.7}
              fontFamily={GlobalStyles.text.fontFamily}
            >
              {w}
            </Text>
          ))}
        </HStack>

        {weeks.map((week, wi) => (
          <HStack key={wi} justifyContent="space-between" mb={1}>
            {week.map((cell, ci) => {
              if (!cell) {
                return <Box key={ci} w="14%" h={34} />;
              }
              const selectedStart = isSameDay(cell, start);
              const selectedEnd = isSameDay(cell, end);
              const between = inRange(cell);

              let cellStyle = {
                width: "14%",
                height: 34,
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 6,
              };
              let textStyle = {
                fontSize: 14,
                color: GlobalStyles.colors.black,
              };

              if (selectedStart || selectedEnd) {
                cellStyle = { ...cellStyle, backgroundColor: darkBlue };
                textStyle = {
                  ...textStyle,
                  color: GlobalStyles.colors.white,
                  fontWeight: "600",
                };
              } else if (between) {
                cellStyle = { ...cellStyle, backgroundColor: lightRange };
              }

              return (
                <Pressable
                  key={ci}
                  onPress={() => handleSelect(cell)}
                  style={{ width: "14%" }}
                >
                  <Box style={cellStyle}>
                    <Text style={textStyle}>{cell.getDate()}</Text>
                  </Box>
                </Pressable>
              );
            })}
          </HStack>
        ))}
      </Box>
    );
  };

  const [y1, m1] = [viewYear, viewMonth];
  const [y2, m2] = addMonths(viewYear, viewMonth, 1);

  return (
    <Box mt={4} borderWidth={0} borderRadius={8} overflow="hidden">
      <LinearGradient
        colors={[
          GlobalStyles.colors.gradientBlueStart ?? "#000561",
          "rgba(0,63,176,0.79)",
          GlobalStyles.colors.gradientBlueStart ?? "#000561",
        ]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 0 }}
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          padding: 10,
        }}
      >
        <Pressable
          onPress={() => {
            const [ny, nm] = addMonths(viewYear, viewMonth, -1);
            setViewYear(ny);
            setViewMonth(nm);
          }}
          style={{ padding: 6 }}
        >
          <MaterialIcons
            name="keyboard-arrow-left"
            size={26}
            color={GlobalStyles.colors.white}
          />
        </Pressable>

        <ScrollView horizontal showsHorizontalScrollIndicator={false} flex={1}>
          <HStack alignItems="center" space={2} px={2}>
            <Text
              color={GlobalStyles.colors.white}
              fontSize={["12px", "13px", "14px"]}
              fontWeight="600"
              fontFamily={GlobalStyles.text.fontFamily}
            >
              {formatMonthTitle(y1, m1)}
            </Text>
            <MaterialIcons
              name="arrow-drop-down"
              size={20}
              color={GlobalStyles.colors.white}
            />
            <Text
              color={GlobalStyles.colors.white}
              fontSize={["12px", "13px", "14px"]}
              fontWeight="600"
              ml={2}
              fontFamily={GlobalStyles.text.fontFamily}
            >
              {y1}
            </Text>

            <Box w={16} />

            <Text
              color={GlobalStyles.colors.white}
              fontSize={["12px", "13px", "14px"]}
              fontWeight="600"
              fontFamily={GlobalStyles.text.fontFamily}
            >
              {formatMonthTitle(y2, m2)}
            </Text>
            <MaterialIcons
              name="arrow-drop-down"
              size={20}
              color={GlobalStyles.colors.white}
            />
            <Text
              color={GlobalStyles.colors.white}
              fontSize={["12px", "13px", "14px"]}
              fontWeight="600"
              ml={2}
              fontFamily={GlobalStyles.text.fontFamily}
            >
              {y2}
            </Text>
          </HStack>
        </ScrollView>

        <Pressable
          onPress={() => {
            const [ny, nm] = addMonths(viewYear, viewMonth, 1);
            setViewYear(ny);
            setViewMonth(nm);
          }}
          style={{ padding: 6 }}
        >
          <MaterialIcons
            name="keyboard-arrow-right"
            size={26}
            color={GlobalStyles.colors.white}
          />
        </Pressable>
      </LinearGradient>

      <HStack px={4} py={4} flexDirection={["column", "column", "row"]}>
        {renderMonth(y1, m1)}
        {renderMonth(y2, m2)}
      </HStack>

      <HStack
        justifyContent="space-between"
        alignItems="center"
        px={4}
        py={3}
        borderTopWidth={0}
        borderColor={GlobalStyles.colors.black}
        flexDirection={["column", "column", "row"]}
        space={[3, 3, 0]}
      >
        <Button variant="ghost" onPress={onClose} w={["100%", "100%", "auto"]}>
          Close
        </Button>

        <HStack space={3} w={["100%", "100%", "auto"]}>
          <Button
            variant="outline"
            flex={[1, 1, 0]}
            minWidth={[0, 0, "80px"]}
            onPress={() => {
              setStart(null);
              setEnd(null);
            }}
          >
            Clear
          </Button>
          <Button
            flex={[1, 1, 0]}
            minWidth={[0, 0, "80px"]}
            onPress={() => {
              onApply(start, end);
              onClose();
            }}
            isDisabled={!start}
            _text={{ fontWeight: "600" }}
          >
            Apply
          </Button>
        </HStack>
      </HStack>
    </Box>
  );
};

const BusinessSurvey = ({
  onBack,
  initialRoute,
  routeParams,
}) => {
  const toast = useToast();
  const navigation = useNavigation();
  const user = useSelector((state) => state.user?.user);

  useEffect(() => {
    axios.defaults.headers.common = {
      Authorization: `bearer ${user ? user.token : null}`,
    };
  }, [user]);

  // ── Auto-navigate when opened by the agent via ui_action 
  useEffect(() => {
    if (!initialRoute) return;

    switch (initialRoute) {
      case "OPEN_ANALYTICS": {
        const openAnalytics = async () => {
          try {
            setIsLoading(true);
            const res = await axios.get(`${BASE_URL}/api/survey/`);
            if (res.data?.surveys) {
              setAllSurveys(res.data.surveys);

              const targetId   = routeParams?.survey_id;
              const targetName = routeParams?.survey_name;
              if (targetId) {
                const targetSurvey =
                  res.data.surveys.find((s) => s.id === targetId) ||
                  { id: targetId, name: targetName || "" };

                setSelectedSurveyForAnalytics(targetSurvey);
                const fetchedQuestions = await fetchQuestionsForSurvey(targetId);
                await fetchAnalytics(targetId, null, null, fetchedQuestions);
              }
            }
          } catch (err) {
            console.error("Auto-open analytics error:", err);
          } finally {
            setIsLoading(false);
          }
          setIsHome(false);
          setIsAnalytics(true);
        };
        openAnalytics();
        break;
      }

      case "OPEN_SURVEY": {
        const openSurvey = async () => {
          const targetId = routeParams?.survey_id;
          if (targetId) {
            setSurveyId(targetId);
            await fetchQuestionsForSurvey(targetId);
          }
          setIsHome(false);
          setIsPreview(true);
        };
        openSurvey();
        break;
      }

      case "OPEN_QUESTIONS": {
        const openQuestions = async () => {
          const targetId = routeParams?.survey_id;
          if (targetId) {
            setSurveyId(targetId);
            await fetchQuestionsForSurvey(targetId);
          }
          // Land on the editable questions / create-survey view
          setIsHome(false);
          setIsPreview(false);
        };
        openQuestions();
        break;
      }

      case "OPEN_SURVEY_EDITOR": {
        const openEditor = async () => {
          const targetId = routeParams?.survey_id;
          if (targetId) {
            setSurveyId(targetId);
            await fetchQuestionsForSurvey(targetId);
          }
          setIsHome(false);
          setIsPreview(false);
        };
        openEditor();
        break;
      }

      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRoute]);

  const [surveyId, setSurveyId] = useState(null);
  const [questionIds, setQuestionIds] = useState([]);
  const [title, setTitle] = useState("Rate Our Service from 1 to 5");
  const [subtitle, setSubtitle] = useState("Subheading or subtitle");
  const [questions, setQuestions] = useState([
    "How did you like our receptionist?",
    "How do you rate our ambience?",
    "How do you rate freshness of our food?",
  ]);

  const [isHome, setIsHome] = useState(true);
  const [isPreview, setIsPreview] = useState(false);
  const [isAnalytics, setIsAnalytics] = useState(false);
  const [isAnalyticsChart, setIsAnalyticsChart] = useState(false);
  const [isDateView, setIsDateView] = useState(false);
  const [isSurveyList, setIsSurveyList] = useState(false);

  const [allSurveys, setAllSurveys] = useState([]);
  const [selectedSurveyForAnalytics, setSelectedSurveyForAnalytics] = useState(null);
  const [analyticsData, setAnalyticsData] = useState({ questions: [] });
  const [calendarRange, setCalendarRange] = useState({
    start: null,
    end: null,
  });
  const [previewStars] = useState([0, 0, 0, 0, 0]);

  const [editingIndex, setEditingIndex] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const cardWidthProp = ["92%", "85%", 650];


  const fetchAllSurveys = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(`${BASE_URL}/api/survey/`);
      if (res.data?.surveys) {
        setAllSurveys(res.data.surveys);
      }
    } catch (err) {
      console.error("Error fetching surveys:", err);
      toast.show({
        render: () => (
          <ToastMessage
            color={GlobalStyles.colors.red}
            message={"Failed to fetch surveys!"}
          />
        ),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchQuestionsForSurvey = async (surveyId) => {
    try {
      console.log("Fetching questions for survey:", surveyId);
      
      const questionUrl = `${BASE_URL}/api/question/${surveyId}`;
      console.log("Question URL:", questionUrl);
      
      const res = await axios.get(questionUrl);
      console.log("Questions API response:", res.data);
      
      let questionsArray = [];
      
      if (res.data?.questions && Array.isArray(res.data.questions)) {
        questionsArray = res.data.questions;
      } else if (res.data?.data && Array.isArray(res.data.data)) {
        questionsArray = res.data.data;
      } else if (Array.isArray(res.data)) {
        questionsArray = res.data;
      }
      
      console.log("Parsed questions array:", questionsArray);
      console.log("Number of questions:", questionsArray.length);
      
      const filteredQuestions = questionsArray.filter(q => q.survey_id === surveyId);
      console.log("Filtered questions for survey ID", surveyId, ":", filteredQuestions);
      
      if (filteredQuestions.length > 0) {
        setQuestions(filteredQuestions.map(q => q.text));
        setQuestionIds(filteredQuestions.map(q => q.id));
        console.log("Questions set successfully:", filteredQuestions.length);
        return filteredQuestions;
      } else {
        console.log("No questions found for this survey");
        setQuestions([]);
        setQuestionIds([]);
        
        toast.show({
          render: () => (
            <ToastMessage
              color={GlobalStyles.colors.yellow || "#FFA500"}
              message={"No questions found for this survey"}
            />
          ),
        });
        return [];
      }
    } catch (err) {
      console.error("Error fetching questions:", err);
      console.error("Error response:", err.response);
      console.error("Error data:", err.response?.data);
      console.error("Error status:", err.response?.status);
      
      setQuestions([]);
      setQuestionIds([]);
      
      toast.show({
        render: () => (
          <ToastMessage
            color={GlobalStyles.colors.red}
            message={err.response?.data?.message || err.response?.data?.error || "Failed to load survey questions!"}
          />
        ),
      });
      return [];
    }
  };

  const buildQuestionTextMap = (questionObjects) => {
    const map = {};
    if (Array.isArray(questionObjects)) {
      questionObjects.forEach(q => {
        if (q.id) map[q.id] = q.text || q.question_text || "";
      });
    }
    return map;
  };

  
  const mergeQuestionTexts = (analyticsItems, questionTextMap, fallbackQuestions) => {
    return analyticsItems.map((item, idx) => {
      const textById = item.id ? questionTextMap[item.id] : null;
      const textByPosition = fallbackQuestions[idx] || "";
      const resolvedText = item.text || item.question_text || textById || textByPosition || "Question";
      return {
        ...item,
        text: resolvedText,
        question_text: resolvedText,
      };
    });
  };

  const fetchAnalytics = async (surveyIdToFetch, startDate = null, endDate = null, preloadedQuestions = null) => {
    try {
      setIsLoading(true);
      
      if (!surveyIdToFetch) {
        throw new Error("Survey ID is required");
      }

     
      let questionTextMap = {};
      let fallbackQuestionTexts = [];

      if (preloadedQuestions && preloadedQuestions.length > 0) {
        questionTextMap = buildQuestionTextMap(preloadedQuestions);
        fallbackQuestionTexts = preloadedQuestions.map(q => q.text || "");
      } else {
        try {
          const qRes = await axios.get(`${BASE_URL}/api/question/${surveyIdToFetch}`);
          let qArray = [];
          if (qRes.data?.questions && Array.isArray(qRes.data.questions)) {
            qArray = qRes.data.questions;
          } else if (qRes.data?.data && Array.isArray(qRes.data.data)) {
            qArray = qRes.data.data;
          } else if (Array.isArray(qRes.data)) {
            qArray = qRes.data;
          }
          const filtered = qArray.filter(q => q.survey_id === surveyIdToFetch);
          const source = filtered.length > 0 ? filtered : qArray;
          questionTextMap = buildQuestionTextMap(source);
          fallbackQuestionTexts = source.map(q => q.text || "");
        } catch (qErr) {
          console.warn("Could not fetch question texts for analytics:", qErr);
        }
      }
      
      let url = `${BASE_URL}/api/stat/${surveyIdToFetch}`;
      
      if (startDate && endDate) {
        const startDateObj = new Date(startDate);
        const endDateObj = new Date(endDate);
        
        if (isNaN(startDateObj.getTime()) || isNaN(endDateObj.getTime())) {
          throw new Error("Invalid date format");
        }
        
        const start = startDateObj.toISOString().split('T')[0];
        const end = endDateObj.toISOString().split('T')[0];
        url = `${BASE_URL}/api/stat?startDate=${start}&endDate=${end}&survey_id=${surveyIdToFetch}`;
      }

      console.log("Fetching analytics from URL:", url);
      const res = await axios.get(url);
      console.log("Analytics API raw response:", res.data);
      
      if (res.data) {
        if (res.data.data && Array.isArray(res.data.data)) {
          const rawItems = res.data.data.map(item => ({
            id: item.id,
            text: item.text,
            question_text: item.text,
            survey_id: item.survey_id,
            status: item.status,
            created_at: item.created_at,
            updated_at: item.updated_at,
            ratings: item.totalRatings || item.ratings || {},
            totalRatings: item.totalRatings || item.ratings || {},
          }));
          const mergedItems = mergeQuestionTexts(rawItems, questionTextMap, fallbackQuestionTexts);
          console.log("Merged analytics data:", mergedItems);
          setAnalyticsData({ questions: mergedItems });
        } else if (res.data.questions && Array.isArray(res.data.questions)) {
          const merged = mergeQuestionTexts(res.data.questions, questionTextMap, fallbackQuestionTexts);
          setAnalyticsData({ questions: merged });
        } else {
          if (fallbackQuestionTexts.length > 0) {
            const fallbackItems = fallbackQuestionTexts.map((text, idx) => ({
              id: Object.keys(questionTextMap)[idx] || idx,
              text,
              question_text: text,
              ratings: {},
              totalRatings: {},
            }));
            setAnalyticsData({ questions: fallbackItems });
          } else {
            setAnalyticsData({ questions: [] });
          }
        }
      }
    } catch (err) {
      console.error("Error fetching analytics:", err);
      console.error("Error response data:", err.response?.data);
      
      if (startDate && endDate) {
        console.log("Date range query failed, fetching all data...");
        try {
          const fallbackUrl = `${BASE_URL}/api/stat/${surveyIdToFetch}`;
          const fallbackRes = await axios.get(fallbackUrl);
          
          if (fallbackRes.data?.data && Array.isArray(fallbackRes.data.data)) {
            const rawItems = fallbackRes.data.data.map(item => ({
              id: item.id,
              text: item.text,
              question_text: item.text,
              ratings: item.totalRatings || {},
              totalRatings: item.totalRatings || {},
            }));
            const merged = mergeQuestionTexts(rawItems, {}, []);
            setAnalyticsData({ questions: merged });
            setIsAnalyticsChart(true);
            toast.show({
              render: () => (
                <ToastMessage
                  color={GlobalStyles.colors.yellow || "#FFA500"}
                  message={"Date filtering not available. Showing all data."}
                />
              ),
            });
            return;
          }
        } catch (fallbackErr) {
          console.error("Fallback also failed:", fallbackErr);
        }
      }
      
      toast.show({
        render: () => (
          <ToastMessage
            color={GlobalStyles.colors.red}
            message={err.response?.data?.message || "Failed to fetch analytics!"}
          />
        ),
      });
      setAnalyticsData({ questions: [] });
    } finally {
      setIsLoading(false);
    }
  };

  const addRow = useCallback(() => {
    setQuestions((prev) => [...prev, "New question"]);
  }, []);

  const deleteRow = useCallback((index) => {
    setQuestions((prev) => prev.filter((_, i) => i !== index));
    setQuestionIds((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const updateQuestion = useCallback((index, text) => {
    setQuestions((prev) => {
      const next = [...prev];
      next[index] = text;
      return next;
    });
  }, []);

  const createSurvey = async () => {
    try {
      const body = {
        name: title,
        heading: subtitle,
        user_id: user?.id,
      };
      const res = await axios.post(`${BASE_URL}/api/survey/`, body);

      if (res.data?.survey?.id) {
        setSurveyId(res.data.survey.id);
        return res.data.survey.id;
      }
      return null;
    } catch (err) {
      console.error("Survey Creation Error:", err);
      toast.show({
        render: () => (
          <ToastMessage
            color={GlobalStyles.colors.red}
            message={"Failed to create survey!"}
          />
        ),
      });
      return null;
    }
  };

  const updateSurvey = async () => {
    if (!surveyId) return;

    try {
      const body = {
        name: title,
        heading: subtitle,
        status: "ACTIVE",
      };
      await axios.put(`${BASE_URL}/api/survey/${surveyId}`, body);
    } catch (err) {
      console.error("Survey update error:", err);
      toast.show({
        render: () => (
          <ToastMessage
            color={GlobalStyles.colors.red}
            message={"Failed to update survey!"}
          />
        ),
      });
    }
  };

  const syncQuestions = async (sId) => {
    const ids = [...questionIds];

    for (let i = 0; i < questions.length; i++) {
      const qText = questions[i];

      if (ids[i]) {
        try {
          const body = { text: qText, status: "ACTIVE" };
          await axios.put(`${BASE_URL}/api/question/${ids[i]}`, body);
        } catch (err) {
          console.error("Question update error:", err);
        }
      } else {
        try {
          const body = { text: qText };
          const res = await axios.post(`${BASE_URL}/api/question/${sId}`, body);
          if (res.data?.question?.id) {
            ids[i] = res.data.question.id;
          }
        } catch (err) {
          console.error("Question creation error:", err);
        }
      }
    }
    setQuestionIds(ids);
  };

  const handlePreviewPress = async () => {
    if (!surveyId) {
      const id = await createSurvey();
      if (id) {
        setIsPreview(true);
        toast.show({
          render: () => (
            <ToastMessage
              color={GlobalStyles.colors.green}
              message={"Survey created!"}
            />
          ),
        });
        syncQuestions(id).catch((err) => console.error("Error syncing questions:", err));
      }
    } else {
      setIsPreview(true);
      toast.show({
        render: () => (
          <ToastMessage
            color={GlobalStyles.colors.green}
            message={"Survey updated!"}
          />
        ),
      });
      updateSurvey().catch((err) => console.error("Error updating survey:", err));
      syncQuestions(surveyId).catch((err) => console.error("Error syncing questions:", err));
    }
  };

  if (isHome) {
    return (
      <>
        <StatusBar
          backgroundColor={GlobalStyles.colors.blue}
          barStyle="light-content"
        />
        <ScrollView flex={1} bg="muted.100">
          <Center>
            <Stack
              mt={4}
              w={cardWidthProp}
              alignItems="center"
              justifyContent="center"
            >
              {onBack && (
                <HStack w="100%" mb={2} px={[2, 4, 4]}>
                  <NBPressable onPress={onBack}>
                    <HStack alignItems="center" space={2}>
                      <Text
                        color={GlobalStyles.colors.blue}
                        fontWeight="500"
                        fontSize={[13, 14, 14]}
                        fontFamily={GlobalStyles.text.fontFamily}
                      >
                        ← Back to Services
                      </Text>
                    </HStack>
                  </NBPressable>
                </HStack>
              )}

              <Box
                w="100%"
                bg={GlobalStyles.colors.white}
                borderRadius={12}
                px={[4, 5, 6]}
                py={[3, 4, 4]}
                mb={4}
              >
                <VStack space={3}>
                  <HStack justifyContent="space-between" alignItems="center">
                    <Text
                      fontSize={["16px", "18px", "20px"]}
                      fontWeight="500"
                      color={GlobalStyles.colors.black}
                      fontFamily={GlobalStyles.text.fontFamily}
                    >
                      Survey Management
                    </Text>
                    <HomeButton onPress={() => {}} />
                  </HStack>

                  <Text
                    fontSize={["12px", "13px", "14px"]}
                    color={GlobalStyles.colors.black}
                    fontFamily={GlobalStyles.text.fontFamily}
                    opacity={0.7}
                  >
                    Choose an option to get started
                  </Text>

                  <HStack
                    justifyContent="space-between"
                    w="100%"
                    space={[3, 3, 3]}
                    flexDirection={["column", "column", "row"]}
                  >
                    <GradientButton
                      onPress={() => {
                        fetchAllSurveys();
                        setIsHome(false);
                        setIsSurveyList(true);
                      }}
                    >
                      Current Surveys
                    </GradientButton>
                    <GradientButton
                      onPress={() => {
                        setIsHome(false);
                      }}
                    >
                      Create New Survey
                    </GradientButton>
                  </HStack>
                </VStack>
              </Box>
            </Stack>
          </Center>
        </ScrollView>
      </>
    );
  }

  if (isSurveyList) {
    return (
      <>
        <StatusBar
          backgroundColor={GlobalStyles.colors.blue}
          barStyle="light-content"
        />
        <ScrollView flex={1} bg="muted.100">
          <Center>
            <Stack
              mt={4}
              w={cardWidthProp}
              alignItems="center"
              justifyContent="center"
            >
              <Box
                w="100%"
                bg={GlobalStyles.colors.white}
                borderRadius={12}
                p={[4, 5, 6]}
                mb={6}
              >
                <VStack space={3}>
                  <HStack justifyContent="space-between" alignItems="center" mb={1}>
                    <Text
                      fontSize={["18px", "20px", "22px"]}
                      fontWeight="500"
                      color={GlobalStyles.colors.black}
                      fontFamily={GlobalStyles.text.fontFamily}
                    >
                      Current Surveys
                    </Text>
                    <HomeButton onPress={() => {
                      setIsSurveyList(false);
                      setIsHome(true);
                    }} />
                  </HStack>

                  <HStack
                    w="100%"
                    alignItems="center"
                    space={2}
                    mb={1}
                    px={1}
                  >
                    <Box flex={1}>
                      <Text
                        fontSize="12px"
                        fontWeight="600"
                        color={GlobalStyles.colors.black}
                        fontFamily={GlobalStyles.text.fontFamily}
                      >
                        Survey Name
                      </Text>
                    </Box>
                    <Box w="100px" alignItems="center">
                      <Text
                        fontSize="12px"
                        fontWeight="600"
                        color={GlobalStyles.colors.black}
                        fontFamily={GlobalStyles.text.fontFamily}
                      >
                        Created Date
                      </Text>
                    </Box>
                    <Box w="50px" />
                    <Box w="90px" />
                    <Box w="40px" />
                  </HStack>

                  {isLoading ? (
                    <Text
                      fontSize={["14px", "15px", "16px"]}
                      color={GlobalStyles.colors.black}
                      fontFamily={GlobalStyles.text.fontFamily}
                    >
                      Loading...
                    </Text>
                  ) : allSurveys.length === 0 ? (
                    <Text
                      fontSize={["14px", "15px", "16px"]}
                      color={GlobalStyles.colors.black}
                      fontFamily={GlobalStyles.text.fontFamily}
                    >
                      No surveys found.
                    </Text>
                  ) : (
                    allSurveys.map((survey) => (
                      <HStack
                        key={survey.id}
                        w="100%"
                        alignItems="center"
                        space={2}
                        mb={2}
                      >
                        <Box
                          flex={1}
                          borderWidth={1}
                          borderColor={GlobalStyles.colors.black}
                          borderRadius={8}
                          bg={GlobalStyles.colors.white}
                          px={3}
                          h={`${ROW_HEIGHT}px`}
                          justifyContent="center"
                        >
                          <Text
                            fontSize="13px"
                            fontWeight="400"
                            color={GlobalStyles.colors.black}
                            fontFamily={GlobalStyles.text.fontFamily}
                            numberOfLines={1}
                          >
                            {survey.name}
                          </Text>
                        </Box>

                        <Box
                          borderWidth={1}
                          borderColor={GlobalStyles.colors.black}
                          borderRadius={8}
                          bg={GlobalStyles.colors.white}
                          px={2}
                          h={`${ROW_HEIGHT}px`}
                          justifyContent="center"
                          w="100px"
                          alignItems="center"
                        >
                          <Text
                            fontSize="11px"
                            color={GlobalStyles.colors.black}
                            fontFamily={GlobalStyles.text.fontFamily}
                            textAlign="center"
                          >
                            {survey.created_at
                              ? new Date(survey.created_at).toLocaleDateString('en-US', { 
                                  month: '2-digit', 
                                  day: '2-digit', 
                                  year: 'numeric' 
                                })
                              : "N/A"}
                          </Text>
                        </Box>

                        <Pressable
                          onPress={async () => {
                            console.log("View button clicked for survey:", survey);
                            
                            setSelectedSurveyForAnalytics(survey);
                            setTitle(survey.name);
                            setSubtitle(survey.heading || "");
                            setSurveyId(survey.id);
                            
                            await fetchQuestionsForSurvey(survey.id);
                            
                            setIsSurveyList(false);
                            setIsPreview(true);
                          }}
                          style={{
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: GlobalStyles.colors.white,
                            borderWidth: 1,
                            width: 50,
                            height: ROW_HEIGHT,
                            borderColor: GlobalStyles.colors.black,
                            borderRadius: 10,
                          }}
                        >
                          <MaterialIcons
                            name="visibility"
                            size={20}
                            color={GlobalStyles.colors.black}
                          />
                        </Pressable>

                        <Box
                          borderRadius={10}
                          overflow="hidden"
                          borderWidth={1}
                          borderColor={GlobalStyles.colors.black}
                          bg={GlobalStyles.colors.white}
                          w="90px"
                        >
                          <LinearGradient
                            colors={[
                              GlobalStyles.colors.gradientBlueStart ?? "#000561",
                              GlobalStyles.colors.gradientBlueEnd ?? "#000AC7",
                            ]}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 0 }}
                            style={{
                              width: "100%",
                              height: ROW_HEIGHT,
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: 10,
                            }}
                          >
                            <Pressable
                              onPress={async () => {
                                console.log("Analytics button clicked for survey:", survey);
                                setSelectedSurveyForAnalytics(survey);
                                const fetchedQuestions = await fetchQuestionsForSurvey(survey.id);
                                await fetchAnalytics(survey.id, null, null, fetchedQuestions);
                                
                                setIsSurveyList(false);
                                setIsAnalytics(true);
                              }}
                              style={{
                                width: "100%",
                                height: "100%",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <Text
                                color={GlobalStyles.colors.mustard}
                                fontWeight="500"
                                fontSize="12px"
                                letterSpacing="0.1px"
                                fontFamily={GlobalStyles.text.fontFamily}
                                textAlign="center"
                              >
                                Analytics
                              </Text>
                            </Pressable>
                          </LinearGradient>
                        </Box>
                        <Pressable
                          onPress={() => {
                            axios.delete(`${BASE_URL}/api/survey/${survey.id}`)
                              .then(() => {
                                setAllSurveys(prevSurveys => 
                                  prevSurveys.filter(s => s.id !== survey.id)
                                );
                                toast.show({
                                  render: () => (
                                    <ToastMessage
                                      color={GlobalStyles.colors.green}
                                      message={"Survey deleted successfully!"}
                                    />
                                  ),
                                });
                              })
                              .catch(err => {
                                console.error("Error deleting survey:", err);
                                toast.show({
                                  render: () => (
                                    <ToastMessage
                                      color={GlobalStyles.colors.red}
                                      message={"Failed to delete survey!"}
                                    />
                                  ),
                                });
                              });
                          }}
                          style={{
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: GlobalStyles.colors.white,
                            borderWidth: 1,
                            width: EDIT_BOX_SIZE,
                            height: ROW_HEIGHT,
                            borderColor: GlobalStyles.colors.red || "#FF0000",
                            borderRadius: 10,
                          }}
                        >
                          <MaterialIcons
                            name="delete"
                            size={20}
                            color={GlobalStyles.colors.red || "#FF0000"}
                          />
                        </Pressable>
                      </HStack>
                    ))
                  )}

                  {/* Back button below card */}
                  <HStack mt={4} w="100%" justifyContent="flex-start">
                    <GradientButton onPress={() => {
                      setIsSurveyList(false);
                      setIsHome(true);
                    }}>
                      Back
                    </GradientButton>
                  </HStack>
                </VStack>
              </Box>
            </Stack>
          </Center>
        </ScrollView>
      </>
    );
  }

  if (isDateView) {
    return (
      <>
        <StatusBar
          backgroundColor={GlobalStyles.colors.blue}
          barStyle="light-content"
        />
        <ScrollView flex={1} bg="muted.100">
          <Center>
            <Stack
              mt={4}
              w={cardWidthProp}
              alignItems="center"
              justifyContent="center"
            >
              <Box
                w="100%"
                bg={GlobalStyles.colors.white}
                borderRadius={12}
                p={[4, 5, 6]}
                mb={6}
              >
                <HStack justifyContent="space-between" alignItems="center" mb={4}>
                  <Text
                    fontSize={["18px", "20px", "22px"]}
                    fontWeight="500"
                    color={GlobalStyles.colors.black}
                    fontFamily={GlobalStyles.text.fontFamily}
                  >
                    Select date range
                  </Text>
                  <HomeButton onPress={() => {
                    setIsDateView(false);
                    setIsAnalytics(false);
                    setIsAnalyticsChart(false);
                    setIsHome(true);
                  }} />
                </HStack>

                <CalendarInline
                  initialStart={calendarRange.start}
                  initialEnd={calendarRange.end}
                  onApply={(s, e) => {
                    setCalendarRange({ start: s, end: e });
                    if (selectedSurveyForAnalytics) {
                      fetchAnalytics(selectedSurveyForAnalytics.id, s, e);
                    }
                    setIsDateView(false);
                    setIsAnalytics(true);
                    setIsAnalyticsChart(true);
                  }}
                  onClose={() => {
                    setIsDateView(false);
                    setIsAnalytics(true);
                  }}
                />

                <HStack mt={6} w="100%" justifyContent="flex-start">
                  <GradientButton onPress={() => {
                    setIsDateView(false);
                    setIsAnalytics(true);
                  }}>
                    Back
                  </GradientButton>
                </HStack>
              </Box>
            </Stack>
          </Center>
        </ScrollView>
      </>
    );
  }

  if (isAnalytics && isAnalyticsChart) {
    const chartData = [
      { label: "Star 5", value: 0 },
      { label: "Star 4", value: 0 },
      { label: "Star 3", value: 0 },
      { label: "Star 2", value: 0 },
      { label: "Star 1", value: 0 },
    ];

    if (analyticsData.questions && analyticsData.questions.length > 0) {
      analyticsData.questions.forEach((q) => {
        const ratingsObj = q.totalRatings || q.ratings || {};
        if (ratingsObj && typeof ratingsObj === 'object') {
          Object.entries(ratingsObj).forEach(([rating, count]) => {
            const ratingNum = parseInt(rating);
            const countNum = parseInt(count) || 0;
            if (ratingNum >= 1 && ratingNum <= 5) {
              chartData[5 - ratingNum].value += countNum;
            }
          });
        }
      });
    }

    const max = Math.max(...chartData.map((d) => d.value), 25);
    const ticks = Array.from({ length: 6 }, (_, i) => Math.round((max / 5) * i));

    return (
      <>
        <StatusBar
          backgroundColor={GlobalStyles.colors.blue}
          barStyle="light-content"
        />
        <ScrollView flex={1} bg="muted.100">
          <Center>
            <Stack
              mt={4}
              w={cardWidthProp}
              alignItems="center"
              justifyContent="center"
            >
              <Box
                w="100%"
                bg={GlobalStyles.colors.white}
                borderRadius={12}
                p={[4, 5, 6]}
                mb={6}
              >
                <VStack>
                  <HStack justifyContent="space-between" alignItems="center" mb={4}>
                    <Text
                      fontSize={["18px", "20px", "22px"]}
                      fontWeight="500"
                      color={GlobalStyles.colors.black}
                      fontFamily={GlobalStyles.text.fontFamily}
                    >
                      Analytics
                    </Text>
                    <HomeButton onPress={() => {
                      setIsAnalytics(false);
                      setIsAnalyticsChart(false);
                      setIsHome(true);
                    }} />
                  </HStack>

                  <Text
                    fontSize={["13px", "14px", "14px"]}
                    color={GlobalStyles.colors.black}
                    mb={4}
                    fontFamily={GlobalStyles.text.fontFamily}
                  >
                    Stats
                  </Text>

                  <Box borderWidth={0}>
                    <HStack alignItems="center" justifyContent="flex-start">
                      <VStack
                        space={3}
                        w={["48px", "52px", "56px"]}
                        style={{ marginRight: 2 }}
                      >
                        {chartData.map((d, i) => (
                          <Box key={i} h={36} justifyContent="center">
                            <Text
                              fontSize={["12px", "13px", "14px"]}
                              color={GlobalStyles.colors.black}
                              fontFamily={GlobalStyles.text.fontFamily}
                            >
                              {d.label}
                            </Text>
                          </Box>
                        ))}
                      </VStack>

                      <Box flex={1} maxW={["260px", "380px", "520px"]}>
                        <Box
                          h={220}
                          justifyContent="center"
                          position="relative"
                          style={{ paddingLeft: 0, paddingRight: 6 }}
                        >
                          {ticks.map((t, i) => (
                            <Box
                              key={i}
                              position="absolute"
                              left={`${max > 0 ? (t / max) * 100 : 0}%`}
                              top={0}
                              bottom={0}
                              borderLeftWidth={1}
                              borderLeftColor={GlobalStyles.colors.black}
                              style={{
                                transform: [{ translateX: -0.5 }],
                              }}
                              zIndex={0}
                            />
                          ))}

                          <VStack
                            justifyContent="space-between"
                            h="100%"
                            py={1}
                            style={{ zIndex: 1 }}
                          >
                            {chartData.map((d, idx) => (
                              <HStack key={idx} alignItems="center">
                                <Box flex={1} mr={2}>
                                  <Box h={36} justifyContent="center">
                                    <LinearGradient
                                      colors={[
                                        GlobalStyles.colors.gradientBlueEnd ??
                                          "#000AC7",
                                        GlobalStyles.colors.gradientBlueStart ??
                                          "#000561",
                                      ]}
                                      locations={[0.095, 1]}
                                      start={{ x: 0, y: 0.5 }}
                                      end={{ x: 1, y: 0.5 }}
                                      style={{
                                        width: `${max > 0 ? (d.value / max) * 100 : 0}%`,
                                        height: 12,
                                        borderRadius: 0,
                                      }}
                                    />
                                  </Box>
                                </Box>
                              </HStack>
                            ))}
                          </VStack>

                          {ticks.map((t, i) => (
                            <Text
                              key={i}
                              position="absolute"
                              left={`${max > 0 ? (t / max) * 100 : 0}%`}
                              bottom={-18}
                              fontSize={["10px", "11px", "12px"]}
                              color={GlobalStyles.colors.black}
                              opacity={0.8}
                              style={{ transform: [{ translateX: -8 }] }}
                              zIndex={2}
                              fontFamily={GlobalStyles.text.fontFamily}
                            >
                              {t}
                            </Text>
                          ))}
                        </Box>
                      </Box>
                    </HStack>
                  </Box>

                  <HStack mt={6} w="100%" justifyContent="flex-start">
                    <GradientButton
                      onPress={() => {
                        setIsAnalyticsChart(false);
                        setIsAnalytics(true);
                      }}
                    >
                      Back
                    </GradientButton>
                  </HStack>
                </VStack>
              </Box>
            </Stack>
          </Center>
        </ScrollView>
      </>
    );
  }

  if (isAnalytics) {
    const hasRealText = (q) => q.text && q.text.trim() !== "" && q.text !== "Question";
    const analyticsHasText = analyticsData.questions && analyticsData.questions.length > 0 && analyticsData.questions.some(hasRealText);

    const questionsToShow = analyticsHasText
      ? analyticsData.questions
      : questions.map((q, i) => ({
          id: questionIds[i] || i,
          text: q,
          question_text: q,
          ratings: {},
          totalRatings: {},
        }));

    return (
      <>
        <StatusBar
          backgroundColor={GlobalStyles.colors.blue}
          barStyle="light-content"
        />
        <ScrollView flex={1} bg="muted.100">
          <Center>
            <Stack
              mt={4}
              w={cardWidthProp}
              alignItems="center"
              justifyContent="center"
            >
              <Box
                w="100%"
                bg={GlobalStyles.colors.white}
                borderRadius={12}
                p={[4, 5, 6]}
                mb={6}
              >
                {/* Mobile layout */}
                <VStack space={2} display={["flex", "flex", "none"]}>
                  <HStack justifyContent="space-between" alignItems="center" mb={2}>
                    <Text
                      fontSize={["18px", "20px", "22px"]}
                      fontWeight="500"
                      color={GlobalStyles.colors.black}
                      fontFamily={GlobalStyles.text.fontFamily}
                    >
                      Analytics
                    </Text>
                    <HomeButton onPress={() => {
                      setIsAnalytics(false);
                      setIsHome(true);
                    }} />
                  </HStack>

                  {isLoading ? (
                    <Text
                      fontSize={["14px", "15px", "16px"]}
                      color={GlobalStyles.colors.black}
                      fontFamily={GlobalStyles.text.fontFamily}
                    >
                      Loading analytics...
                    </Text>
                  ) : questionsToShow.length === 0 ? (
                    <Text
                      fontSize={["14px", "15px", "16px"]}
                      color={GlobalStyles.colors.black}
                      fontFamily={GlobalStyles.text.fontFamily}
                    >
                      No analytics data available.
                    </Text>
                  ) : (
                    questionsToShow.map((q, idx) => (
                      <HStack
                        key={q.id || q.question_id || idx}
                        w="100%"
                        alignItems="center"
                        space={2}
                        mb={2}
                      >
                        <Box
                          flex={1}
                          borderWidth={1}
                          borderColor={GlobalStyles.colors.black}
                          borderRadius={8}
                          bg={GlobalStyles.colors.white}
                          px={3}
                          h={`${ROW_HEIGHT}px`}
                          justifyContent="center"
                        >
                          <Text
                            fontSize={["12px", "13px", "13px"]}
                            color={GlobalStyles.colors.black}
                            fontFamily={GlobalStyles.text.fontFamily}
                            numberOfLines={1}
                          >
                            {q.text || q.question_text || "Question"}
                          </Text>
                        </Box>

                        <Box alignItems="center" justifyContent="center">
                          <AnalyticsStatBox
                            topImage={topStatImg}
                            bottomImage={bottomStatImg}
                            onPress={() => {
                              setIsDateView(true);
                              setIsAnalytics(false);
                            }}
                          />
                        </Box>
                      </HStack>
                    ))
                  )}
                </VStack>

                <HStack
                  w="100%"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  display={["none", "none", "flex"]}
                >
                  <VStack flex={1} mr={4}>
                    <HStack justifyContent="space-between" alignItems="center" mb={4}>
                      <Text
                        fontSize={["18px", "20px", "22px"]}
                        fontWeight="500"
                        color={GlobalStyles.colors.black}
                        fontFamily={GlobalStyles.text.fontFamily}
                      >
                        Analytics
                      </Text>
                      <HomeButton onPress={() => {
                        setIsAnalytics(false);
                        setIsHome(true);
                      }} />
                    </HStack>

                    {isLoading ? (
                      <Text
                        fontSize={["14px", "15px", "16px"]}
                        color={GlobalStyles.colors.black}
                        fontFamily={GlobalStyles.text.fontFamily}
                      >
                        Loading analytics...
                      </Text>
                    ) : questionsToShow.length === 0 ? (
                      <Text
                        fontSize={["14px", "15px", "16px"]}
                        color={GlobalStyles.colors.black}
                        fontFamily={GlobalStyles.text.fontFamily}
                      >
                        No analytics data available.
                      </Text>
                    ) : (
                      questionsToShow.map((q, idx) => (
                        <Box
                          key={q.id || q.question_id || idx}
                          borderWidth={1}
                          borderColor={GlobalStyles.colors.black}
                          borderRadius={8}
                          bg={GlobalStyles.colors.white}
                          px={4}
                          h={`${ROW_HEIGHT}px`}
                          justifyContent="center"
                          mb={3}
                        >
                          <Text
                            fontSize={["14px", "15px", "16px"]}
                            color={GlobalStyles.colors.black}
                            fontFamily={GlobalStyles.text.fontFamily}
                          >
                            {q.text || q.question_text || "Question"}
                          </Text>
                        </Box>
                      ))
                    )}
                  </VStack>

                  {questionsToShow.length > 0 && (
                    <VStack w={`${STAT_BOX_SIZE}px`} alignItems="center">
                      <Text
                        fontSize={["16px", "17px", "18px"]}
                        fontWeight="500"
                        color={GlobalStyles.colors.black}
                        mb={4}
                        fontFamily={GlobalStyles.text.fontFamily}
                      >
                        Stats
                      </Text>

                      {questionsToShow.map((_, idx) => (
                        <Box
                          key={idx}
                          mb={4}
                          alignItems="center"
                          justifyContent="center"
                        >
                          <AnalyticsStatBox
                            topImage={topStatImg}
                            bottomImage={bottomStatImg}
                            onPress={() => {
                              setIsDateView(true);
                              setIsAnalytics(false);
                            }}
                          />
                        </Box>
                      ))}
                    </VStack>
                  )}
                </HStack>

                <HStack mt={6} w="100%" justifyContent="flex-start">
                  <GradientButton
                    onPress={() => {
                      setIsAnalytics(false);
                      setIsSurveyList(true);
                    }}
                  >
                    Back
                  </GradientButton>
                </HStack>
              </Box>
            </Stack>
          </Center>
        </ScrollView>
      </>
    );
  }

  return (
    <>
      <StatusBar
        backgroundColor={GlobalStyles.colors.blue}
        barStyle="light-content"
      />
      <ScrollView flex={1} bg="muted.100" keyboardShouldPersistTaps="handled">
        <Center>
          <Stack
            mt={4}
            w={cardWidthProp}
            alignItems="center"
            justifyContent="center"
          >
            <Box
              w="100%"
              bg={GlobalStyles.colors.white}
              borderRadius={12}
              p={[4, 5, 6]}
              mb={6}
            >
              <VStack space={2}>
                {!isPreview ? (
                  <>
                    <HStack justifyContent="space-between" alignItems="center" mb={2}>
                      <Text
                        fontSize={["16px", "18px", "20px"]}
                        fontWeight="500"
                        color={GlobalStyles.colors.black}
                        fontFamily={GlobalStyles.text.fontFamily}
                      >
                        Create Survey
                      </Text>
                      <HomeButton onPress={() => setIsHome(true)} />
                    </HStack>

                    <SurveyRow
                      value={title}
                      onChange={(text) => setTitle(text)}
                      placeholder="Enter Survey Name"
                      isTitle
                      isEditing={editingIndex === "title"}
                      onEditPress={() => setEditingIndex("title")}
                      onSubmitEditing={() => setEditingIndex(null)}
                    />

                    <SurveyRow
                      value={subtitle}
                      onChange={(text) => setSubtitle(text)}
                      placeholder="Subheading or subtitle"
                      isSubtitle
                      isEditing={editingIndex === "subtitle"}
                      onEditPress={() => setEditingIndex("subtitle")}
                      onSubmitEditing={() => setEditingIndex(null)}
                    />

                    <VStack space={2}>
                      {questions.map((q, index) => (
                        <SurveyRow
                          key={index}
                          value={q}
                          onChange={(txt) => updateQuestion(index, txt)}
                          placeholder={`Question ${index + 1}`}
                          isEditing={editingIndex === index}
                          onEditPress={() => setEditingIndex(index)}
                          onSubmitEditing={() => setEditingIndex(null)}
                          showDelete={questions.length > 1}
                          onDeletePress={() => deleteRow(index)}
                        />
                      ))}
                    </VStack>

                    <Box mt={4} w="100%">
                      <HStack
                        justifyContent="space-between"
                        w="100%"
                        space={[4, 4, 3]}
                        flexDirection={["column", "column", "row"]}
                      >
                        <GradientButton onPress={addRow}>
                          Add a row
                        </GradientButton>
                        <GradientButton onPress={handlePreviewPress}>
                          Preview
                        </GradientButton>
                      </HStack>
                    </Box>

                    {/* Back button */}
                    <HStack mt={3} w="100%" justifyContent="flex-start">
                      <GradientButton onPress={() => setIsHome(true)}>
                        Back
                      </GradientButton>
                    </HStack>
                  </>
                ) : (
                  <>
                    <HStack justifyContent="space-between" alignItems="center" mb={2}>
                      <Text
                        fontSize={["18px", "20px", "22px"]}
                        fontWeight="500"
                        color={GlobalStyles.colors.black}
                        fontFamily={GlobalStyles.text.fontFamily}
                      >
                        Preview
                      </Text>
                      <HomeButton onPress={() => setIsHome(true)} />
                    </HStack>

                    <PreviewRow text={title} withStars={false} isTitle />

                    {questions.length === 0 ? (
                      <Text
                        fontSize={["14px", "15px", "16px"]}
                        color={GlobalStyles.colors.black}
                        fontFamily={GlobalStyles.text.fontFamily}
                        mt={2}
                      >
                        No questions available for this survey.
                      </Text>
                    ) : (
                      questions.map((q, idx) => (
                        <PreviewRow
                          key={idx}
                          text={q}
                          withStars
                          stars={previewStars[idx] ?? 0}
                        />
                      ))
                    )}

                    <Box mt={4} w="100%">
                      <HStack
                        justifyContent="space-between"
                        w="100%"
                        space={[4, 4, 3]}
                        flexDirection={["column", "column", "row"]}
                      >
                        <GradientButton
                          onPress={() => setIsPreview(false)}
                        >
                          Edit
                        </GradientButton>
                        <GradientButton
                          onPress={() => {
                            if (surveyId) {
                              updateSurvey().then(() => {
                                syncQuestions(surveyId).then(() => {
                                  setIsPreview(false);
                                  setIsHome(true);
                                  toast.show({
                                    render: () => (
                                      <ToastMessage
                                        color={GlobalStyles.colors.green}
                                        message={"Survey saved successfully!"}
                                      />
                                    ),
                                  });
                                });
                              });
                            } else {
                              setIsPreview(false);
                              setIsHome(true);
                              toast.show({
                                render: () => (
                                  <ToastMessage
                                    color={GlobalStyles.colors.green}
                                    message={"Survey saved successfully!"}
                                  />
                                ),
                              });
                            }
                          }}
                        >
                          Save Survey
                        </GradientButton>
                      </HStack>
                    </Box>

                    {/* Back button for preview */}
                    <HStack mt={3} w="100%" justifyContent="flex-start">
                      <GradientButton onPress={() => {
                        setIsPreview(false);
                        setIsSurveyList(true);
                      }}>
                        Back to Surveys
                      </GradientButton>
                    </HStack>
                  </>
                )}
              </VStack>
            </Box>
          </Stack>
        </Center>
      </ScrollView>
    </>
  );
};
export default BusinessSurvey;